import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import heroImage from '../assets/r19.webp';
import claimImage from '../assets/r18.png';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const ComplianceAgent = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Healthcare Compliance Management | AI RCM | MDeRCM</title>
        <meta 
          name="description" 
          content="AI-powered healthcare compliance management. Spot risks before audits, ensure HIPAA compliance and protect your practice revenue automatically." 
        />
        <meta 
          name="keywords" 
          content="healthcare compliance management, compliance monitoring, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, medical billing outsourcing services, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, healthcare cost reduction strategies, how to improve revenue cycle management, outsourced revenue cycle management services, revenue cycle management services for hospitals, medical billing and coding outsourcing, revenue cycle management services companies" 
        />
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/compliance-agent" />
        <meta property="og:title" content="Healthcare Compliance Management | Revenue Cycle Management Services" />
        <meta property="og:description" content="AI compliance monitoring with 98% reduction in errors and 24/7 audit readiness. Leading healthcare revenue cycle management services." />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/compliance-agent" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mdercm.com/assets/compliance-agent-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Healthcare Compliance Management | AI RCM" />
        <meta name="twitter:description" content="98% reduction in compliance errors with 24/7 audit readiness." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* BREADCRUMB NAVIGATION */}
      <nav 
        aria-label="Breadcrumb"
        style={{ 
          padding: '20px 5%', 
          backgroundColor: '#f7f9fb',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        <ol style={{ 
          display: 'flex', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '8px',
          fontSize: '14px',
          color: '#666',
          listStyle: 'none',
          margin: 0,
          padding: 0
        }}>
          <li>
            <Link 
              to="/" 
              style={{ 
                color: '#666', 
                textDecoration: 'none',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#F26522'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              Home
            </Link>
          </li>
          <li style={{ color: '#999' }}>/</li>
          <li>
            <Link 
              to="/healthcare/ai" 
              style={{ 
                color: '#666', 
                textDecoration: 'none',
                transition: 'color 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#F26522'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              AI Solutions
            </Link>
          </li>
          <li style={{ color: '#999' }}>/</li>
          <li>
            <span style={{ color: '#F26522', fontWeight: '600' }}>Compliance Management</span>
          </li>
        </ol>
      </nav>

      {/* HERO SECTION */}
      <motion.section
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.5)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '650px', zIndex: 2 }}>
          <motion.h1 style={{ fontSize: 'clamp(24px, 5vw, 42px)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.4 }}>
            Healthcare Compliance Management - Stay Compliant. Stay Confident.
          </motion.h1>
          <motion.p style={{ fontSize: 'clamp(14px, 2vw, 18px)', marginBottom: '30px', color: '#dddddd' }}>
            Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> offering AI-powered compliance monitoring. Our <strong>healthcare revenue cycle management services</strong> ensure every credential, policy, and audit trail is continuously monitored with proven <strong>healthcare cost reduction strategies</strong>.
          </motion.p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {[
              ['98%', 'Reduction in Compliance Errors'],
              ['24/7', 'Continuous Audit Readiness'],
              ['80%', 'Time Saved on Manual Checks']
            ].map(([value, label], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{value}</div>
                <div style={{ fontSize: '16px', color: '#ffffff' }}>{label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* OVERVIEW SECTION */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
          <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '30px'
        }}>
          Best Revenue Cycle Management Companies - Spot Compliance Risks Early. Resolve Them Instantly.
        </h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '18px', 
          color: '#555', 
          marginBottom: '50px',
          maxWidth: '900px',
          margin: '0 auto 50px'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, we provide comprehensive <Link to="/healthcare/ai" style={{ color: '#F26522', textDecoration: 'none' }}>revenue cycle management consulting services</Link> that include AI-powered compliance monitoring and <strong>medical billing outsourcing services</strong>.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px'
        }}>
          <div style={{ flex: '1 1 500px', minWidth: '280px', maxWidth: '600px' }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
              Hospital Revenue Cycle Management - Monitor, Alert, and Fix in Real Time
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link>'s Compliance AI Agent continuously scans your workflows, policies, staff credentials, and documentation. When a gap appears—like an expiring license or a missing contract—it alerts the right team and recommends immediate corrective action.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginBottom: '20px' }}>
              Our <strong>revenue cycle management services for hospitals</strong> implement proven <strong>healthcare cost reduction strategies</strong> by automating compliance monitoring. Learn <strong>how to improve revenue cycle management</strong> with intelligent AI that reduces compliance errors by 98% and ensures 24/7 audit readiness.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>
              <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>MDeRCM</Link>'s <strong>outsourced revenue cycle management services</strong> combine <strong>medical billing and coding outsourcing</strong> with advanced compliance automation to create a comprehensive solution that minimizes risk and maximizes operational efficiency.
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
            }}
          >
            <img 
              src={claimImage} 
              alt="Healthcare Compliance Management for Revenue Cycle" 
              style={{ width: '100%', height: 'auto', display: 'block' }}
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.section>

      {/* KEY FEATURES - 4x2 GRID */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#f7f9fb' 
      }}>
        <style>{`
          .compliance-features-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            max-width: 1400px;
            margin: 0 auto;
          }
          @media (max-width: 1200px) {
            .compliance-features-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          @media (max-width: 900px) {
            .compliance-features-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 600px) {
            .compliance-features-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          Revenue Cycle Management Services USA - Key Compliance Features
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          Our <strong>revenue cycle management services USA</strong> provide intelligent compliance monitoring designed for <strong>hospital revenue cycle management</strong> excellence.
        </p>

        <div className="compliance-features-grid">
          {[
            {
              icon: '🔍',
              title: 'Continuous Monitoring',
              desc: 'AI-powered 24/7 surveillance of credentials, policies, documentation, and regulatory changes.'
            },
            {
              icon: '⚠️',
              title: 'Real-Time Alerts',
              desc: 'Instant notifications when compliance issues arise, with recommended corrective actions.'
            },
            {
              icon: '📋',
              title: 'Credentialing Tracking',
              desc: 'Automated monitoring of provider licenses, certifications, and credential expiration dates.'
            },
            {
              icon: '📊',
              title: 'Audit Trail Management',
              desc: 'Complete documentation of all activities, changes, and compliance checks for audit readiness.'
            },
            {
              icon: '🔐',
              title: 'HIPAA Compliance',
              desc: 'Continuous monitoring of HIPAA requirements with automated privacy and security checks.'
            },
            {
              icon: '📜',
              title: 'Policy Management',
              desc: 'Track policy updates, staff acknowledgments, and ensure organization-wide compliance.'
            },
            {
              icon: '🎯',
              title: 'Risk Assessment',
              desc: 'AI identifies potential compliance risks before they become critical issues or penalties.'
            },
            {
              icon: '📈',
              title: 'Compliance Reporting',
              desc: 'Real-time dashboards and detailed reports showing compliance status and improvement trends.'
            }
          ].map((feature, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '35px 25px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                boxShadow: '0 10px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-8px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{feature.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '12px' 
              }}>
                {feature.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6'
              }}>
                {feature.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS PROCESS - 4 STEPS */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#ffffff' 
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '20px',
            color: '#001F2B'
          }}
        >
          How to Improve Revenue Cycle Management with AI Compliance Monitoring
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '800px',
          margin: '0 auto 60px'
        }}>
          Discover <strong>how to improve revenue cycle management</strong> through our proven 4-step AI-powered compliance automation process.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '30px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {[
            {
              step: '01',
              title: 'Scan',
              desc: 'AI continuously scans workflows, credentials, documentation, and policies for compliance gaps.'
            },
            {
              step: '02',
              title: 'Detect',
              desc: 'Machine learning identifies potential issues, violations, and expiring credentials in real-time.'
            },
            {
              step: '03',
              title: 'Alert',
              desc: 'Automated alerts notify relevant teams with specific details and recommended corrective actions.'
            },
            {
              step: '04',
              title: 'Track',
              desc: 'System tracks resolution, maintains audit trails, and generates compliance reports automatically.'
            }
          ].map((step, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, x: i % 2 === 0 ? -60 : 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.2, duration: 0.6 }}
              style={{
                backgroundColor: '#f7f9fb',
                padding: '40px 30px',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#F26522',
                color: '#ffffff',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: '700',
                boxShadow: '0 4px 15px rgba(242, 101, 34, 0.4)'
              }}>
                {step.step}
              </div>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '15px',
                marginTop: '20px'
              }}>
                {step.title}
              </h3>
              <p style={{ 
                fontSize: '16px', 
                color: '#555', 
                lineHeight: '1.7'
              }}>
                {step.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* BENEFITS SECTION - 4x2 GRID */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <style>{`
          .compliance-benefits-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            max-width: 1400px;
            margin: 0 auto;
          }
          @media (max-width: 1200px) {
            .compliance-benefits-grid {
              grid-template-columns: repeat(3, 1fr);
            }
          }
          @media (max-width: 900px) {
            .compliance-benefits-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (max-width: 600px) {
            .compliance-benefits-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Healthcare Cost Reduction Strategies - Measurable Compliance Benefits
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          Implement proven <strong>healthcare cost reduction strategies</strong> through AI-powered compliance automation and achieve measurable results.
        </p>

        <div className="compliance-benefits-grid">
          {[
            {
              icon: '💰',
              title: 'Avoid Costly Penalties',
              desc: 'Prevent compliance violations and avoid fines, penalties, and legal expenses through proactive monitoring.'
            },
            {
              icon: '⏰',
              title: 'Save 80% of Time',
              desc: 'Eliminate manual compliance tracking work, freeing staff to focus on patient care and high-value tasks.'
            },
            {
              icon: '🔒',
              title: 'Reduce Risk Exposure',
              desc: 'Minimize organizational risk with continuous monitoring and early detection of compliance issues.'
            },
            {
              icon: '✅',
              title: 'Ensure Audit Readiness',
              desc: 'Maintain 24/7 audit readiness with complete documentation and automated compliance reporting.'
            },
            {
              icon: '📊',
              title: 'Improve Visibility',
              desc: 'Real-time dashboards provide complete transparency into compliance status across the organization.'
            },
            {
              icon: '🎯',
              title: 'Prevent Revenue Loss',
              desc: 'Stop revenue leakage from compliance-related denials and audit findings before they occur.'
            },
            {
              icon: '📈',
              title: 'Streamline Operations',
              desc: 'Integrate compliance checks into existing workflows for seamless, efficient operations.'
            },
            {
              icon: '🤝',
              title: 'Build Trust',
              desc: 'Demonstrate compliance excellence to payers, patients, and regulatory bodies consistently.'
            }
          ].map((benefit, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '35px 25px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{benefit.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '12px' 
              }}>
                {benefit.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {benefit.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* RELATED SERVICES SECTION */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#f7f9fb' 
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#001F2B'
          }}
        >
          Medical Billing Outsourcing Services - Complete RCM Solutions
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#555', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px'
        }}>
          Explore our comprehensive <strong>medical billing outsourcing services</strong> designed to optimize every aspect of your revenue cycle.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              title: 'Denial Management',
              desc: 'AI-powered denial prevention and appeals management to maximize revenue recovery.',
              link: '/healthcare/ai/denial-management',
              icon: '🔄'
            },
            {
              title: 'Payment Posting',
              desc: 'Automated payment posting processes remittances 50x faster with 99.9% accuracy.',
              link: '/healthcare/ai/payment-posting',
              icon: '💳'
            },
            {
              title: 'Accounts Receivable',
              desc: 'Intelligent A/R management accelerates collections by 70% and reduces outstanding balances.',
              link: '/healthcare/ai/accounts-receivable',
              icon: '💰'
            },
            {
              title: 'Eligibility Verification',
              desc: 'Real-time insurance eligibility checks reduce claim denials and improve first-pass rates.',
              link: '/healthcare/ai/eligibility-check',
              icon: '✓'
            },
            {
              title: 'Prior Authorization',
              desc: 'Automated prior authorization processing accelerates approvals and reduces delays.',
              link: '/healthcare/ai/prior-authorization',
              icon: '📋'
            },
            {
              title: 'Patient Intake',
              desc: 'Streamlined patient registration and demographic verification for accurate billing.',
              link: '/healthcare/ai/patient-intake',
              icon: '👤'
            },
            {
              title: 'Contract Repricing',
              desc: 'Automated insurance contract analysis ensures accurate reimbursement rates.',
              link: '/healthcare/ai/InsuranceContract-Repricing',
              icon: '📊'
            },
            {
              title: 'Policy Verification',
              desc: 'Comprehensive policy status verification prevents coverage-related denials.',
              link: '/healthcare/ai/policy-status-verification',
              icon: '📄'
            }
          ].map((service, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
              whileHover={{ 
                boxShadow: '0 10px 30px rgba(242, 101, 34, 0.2)',
                transform: 'translateY(-8px)'
              }}
            >
              <div style={{ 
                fontSize: '40px', 
                marginBottom: '15px',
                color: '#F26522'
              }}>
                {service.icon}
              </div>
              <h3 style={{ 
                fontSize: '22px', 
                fontWeight: '700', 
                color: '#001F2B', 
                marginBottom: '15px' 
              }}>
                {service.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#555', 
                lineHeight: '1.6',
                marginBottom: '20px',
                flex: 1
              }}>
                {service.desc}
              </p>
              <Link 
                to={service.link}
                style={{ 
                  color: '#F26522', 
                  textDecoration: 'none', 
                  fontWeight: '600',
                  fontSize: '16px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'gap 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.gap = '12px'}
                onMouseOut={(e) => e.currentTarget.style.gap = '8px'}
              >
                Learn More →
              </Link>
            </motion.article>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE MDERCM */}
      <section style={{ 
        padding: '80px 5%', 
        backgroundColor: '#001F2B',
        color: '#ffffff'
      }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}
        >
          Why Choose MDeRCM for Outsourced Revenue Cycle Management Services?
        </motion.h2>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '17px', 
          color: '#dddddd', 
          marginBottom: '60px',
          maxWidth: '900px',
          margin: '0 auto 60px',
          lineHeight: '1.8'
        }}>
          As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading <strong>revenue cycle management services USA</strong> with proven results and dedicated support.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {[
            {
              icon: '🏆',
              title: 'Industry-Leading Expertise',
              desc: 'Over 10+ years of experience in healthcare revenue cycle management with proven track record of success.'
            },
            {
              icon: '🤖',
              title: 'Advanced AI Technology',
              desc: 'Cutting-edge AI and machine learning algorithms that continuously improve accuracy and efficiency.'
            },
            {
              icon: '💵',
              title: 'Maximize Revenue',
              desc: 'Increase revenue by up to 30% through optimized compliance management and reduced regulatory risks.'
            },
            {
              icon: '⚡',
              title: '24/7 Support',
              desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick issue resolution.'
            },
            {
              icon: '🔒',
              title: 'HIPAA Compliant',
              desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy.'
            },
            {
              icon: '📈',
              title: 'Scalable Solutions',
              desc: 'Flexible services that grow with your practice, from small clinics to large hospital systems.'
            },
            {
              icon: '💡',
              title: 'Transparent Reporting',
              desc: 'Real-time dashboards and detailed analytics providing complete visibility into your compliance status.'
            },
            {
              icon: '🎯',
              title: 'Proven ROI',
              desc: 'Measurable results with most clients seeing positive ROI within 3-6 months of implementation.'
            },
          ].map((reason, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                padding: '35px 25px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
              whileHover={{ 
                backgroundColor: 'rgba(242, 101, 34, 0.1)',
                borderColor: '#F26522',
                transform: 'translateY(-5px)'
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>{reason.icon}</div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#ffffff', 
                marginBottom: '12px' 
              }}>
                {reason.title}
              </h3>
              <p style={{ 
                fontSize: '15px', 
                color: '#dddddd', 
                lineHeight: '1.6'
              }}>
                {reason.desc}
              </p>
            </motion.article>
          ))}
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '60px'
        }}>
          <Link 
            to="/free-trial"
            style={{
              backgroundColor: '#F26522',
              color: '#ffffff',
              padding: '18px 50px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '700',
              fontSize: '18px',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#d94d12';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#F26522';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Start Your Free Trial Today
          </Link>
        </div>
      </section>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default ComplianceAgent;